import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, Package, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { updateProduct, deleteProduct } from '../../api/wholesale/companyProducts';

/**
 * EditProductModal - Modal for editing tile code or deleting a product
 */
const EditProductModal = ({ isOpen, onClose, product, onProductUpdated, onProductDeleted }) => {
  const [tileCode, setTileCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when product changes
  useEffect(() => {
    if (product) {
      setTileCode(product.tile_code || '');
      setError(null);
    }
  }, [product?.id]);

  if (!isOpen || !product) {
    return null;
  }

  const hasChanges = tileCode.trim().toUpperCase() !== (product.tile_code || '').toUpperCase();

  const handleSave = async () => {
    const newCode = tileCode.trim().toUpperCase();
    
    if (!newCode) {
      setError('Tile code is required');
      return;
    }

    if (!hasChanges) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const result = await updateProduct(product.id, { tile_code: newCode });
      toast.success(`Tile code updated to "${newCode}"`);
      onProductUpdated?.(result);
      onClose();
    } catch (err) {
      console.error('Error updating product:', err);
      const errorMsg = err.message?.toLowerCase() || '';
      
      // Check for various duplicate/constraint error patterns
      if (
        errorMsg.includes('exists') || 
        errorMsg.includes('duplicate') || 
        errorMsg.includes('unique constraint') ||
        errorMsg.includes('uq_') ||
        err.status === 409
      ) {
        setError(`Tile code "${newCode}" is already in use. Please choose a different code.`);
      } else {
        setError(err.message || 'Failed to update product');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete product "${product.tile_code}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await deleteProduct(product.id);
      toast.success(`Product "${product.tile_code}" deleted`);
      onProductDeleted?.(product.id);
      onClose();
    } catch (err) {
      console.error('Error deleting product:', err);
      setError(err.message || 'Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !saving && !deleting) {
      handleSave();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Edit Product</h2>
              <p className="text-sm text-gray-500">{product.size_category_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Error message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Tile Code Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tile Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={tileCode}
              onChange={(e) => {
                setTileCode(e.target.value.toUpperCase());
                setError(null);
              }}
              onKeyDown={handleKeyDown}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-lg"
              placeholder="Enter tile code"
              autoFocus
            />
            <p className="mt-1 text-xs text-gray-400">
              Tile codes must be unique across all companies
            </p>
          </div>

          {/* Product Info */}
          <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600 space-y-1">
            {product.tile_type_name && (
              <p><span className="text-gray-500">Type:</span> {product.tile_type_name}</p>
            )}
            {product.company_name && (
              <p><span className="text-gray-500">Company:</span> {product.company_name}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleDelete}
            disabled={deleting || saving}
            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            {deleting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            Delete
          </button>
          
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={saving || deleting}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving || deleting}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProductModal;
